import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useCreateInsurance } from "../hooks/insurance";
import type {
  AssetClass,
  CreateInsuranceInput,
  InsuranceKind,
  InsuranceRhythm,
} from "../lib/insurance";

const KINDS: InsuranceKind[] = [
  "KV",
  "HAFTPFLICHT",
  "HAUSRAT",
  "BU",
  "LV",
  "AUTO",
  "RECHTSSCHUTZ",
  "REISE",
  "SONSTIGE",
];
const RHYTHMS: InsuranceRhythm[] = ["MONATLICH", "QUARTALSWEISE", "HALBJAEHRLICH", "JAEHRLICH"];

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function InsuranceNew() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const create = useCreateInsurance();
  const [error, setError] = useState<string | undefined>();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<InsuranceKind>("HAFTPFLICHT");
  const [insurer, setInsurer] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState("");
  const [noticePeriodDays, setNoticePeriodDays] = useState("");
  const [coverageSumEur, setCoverageSumEur] = useState("");
  const [deductibleEur, setDeductibleEur] = useState("");
  const [notes, setNotes] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");

  const [premiumValidFrom, setPremiumValidFrom] = useState(currentYearMonth());
  const [premiumAmountEur, setPremiumAmountEur] = useState("");
  const [premiumRhythm, setPremiumRhythm] = useState<InsuranceRhythm>("JAEHRLICH");
  const [valueAssetClass, setValueAssetClass] = useState<AssetClass | "">("");
  // Mehrere Wertsnapshots schon beim Anlegen erfassen — z.B. Jahresmeldungen
  // 2020/2021/2022 fuer eine alte beitragsfreie LV.
  const [valueSnapshots, setValueSnapshots] = useState<
    { asOf: string; eur: string }[]
  >([{ asOf: todayIso(), eur: "" }]);

  function updateSnapshot(idx: number, patch: { asOf?: string; eur?: string }) {
    setValueSnapshots((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  }
  function addSnapshotRow() {
    setValueSnapshots((prev) => [...prev, { asOf: todayIso(), eur: "" }]);
  }
  function removeSnapshotRow(idx: number) {
    setValueSnapshots((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    const input: CreateInsuranceInput = {
      name: name.trim(),
      kind,
      insurer: insurer.trim(),
      contractNumber: contractNumber.trim() || undefined,
      startDate,
      endDate: endDate || undefined,
      noticePeriodDays: noticePeriodDays ? Number(noticePeriodDays) : undefined,
      coverageSumCents: coverageSumEur ? Math.round(Number(coverageSumEur) * 100) : undefined,
      deductibleCents: deductibleEur ? Math.round(Number(deductibleEur) * 100) : undefined,
      notes: notes.trim() || undefined,
      tags: tagsRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      initialPremium: {
        validFrom: premiumValidFrom,
        amountCents: premiumAmountEur ? Math.round(Number(premiumAmountEur) * 100) : 0,
        rhythm: premiumRhythm,
      },
    };
    if (valueAssetClass) input.valueAssetClass = valueAssetClass;
    try {
      const created = await create.mutateAsync(input);
      // Wenn werthaltig + Snapshots eingetragen: alle valid-rows direkt anlegen.
      if (valueAssetClass) {
        const validSnaps = valueSnapshots
          .map((s) => ({
            asOf: s.asOf,
            cents: s.eur ? Math.round(Number(s.eur) * 100) : 0,
          }))
          .filter((s) => s.asOf && s.cents > 0);
        if (validSnaps.length > 0) {
          const { insuranceApi } = await import("../lib/insurance");
          // sequenziell, damit currentValueCents korrekt auf den juengsten landet
          // (Service raised currentValueCents = letzter Snapshot je asOf)
          for (const s of validSnaps) {
            await insuranceApi.addValueSnapshot(created.id, {
              asOf: s.asOf,
              valueCents: s.cents,
              notes: undefined,
            });
          }
        }
      }
      nav(`/insurance/${created.id}`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="space-y-4 max-w-2xl">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("insurance.new.title")}</h1>
        <Link to="/insurance" className="text-sm text-blue-600 underline">
          {t("insurance.new.back")}
        </Link>
      </header>

      <form onSubmit={handleSubmit} className="bg-white border rounded p-4 space-y-3">
        <Field label={t("insurance.form.name")}>
          <input
            type="text"
            required
            className="border rounded px-2 py-1 w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("insurance.form.kind")}>
            <select
              className="border rounded px-2 py-1 w-full"
              value={kind}
              onChange={(e) => setKind(e.target.value as InsuranceKind)}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`insurance.kind.${k}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("insurance.form.insurer")}>
            <input
              type="text"
              required
              className="border rounded px-2 py-1 w-full"
              value={insurer}
              onChange={(e) => setInsurer(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t("insurance.form.contract_number")}>
          <input
            type="text"
            className="border rounded px-2 py-1 w-full"
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("insurance.form.start_date")}>
            <input
              type="date"
              required
              className="border rounded px-2 py-1 w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field label={t("insurance.form.end_date")}>
            <input
              type="date"
              className="border rounded px-2 py-1 w-full"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("insurance.form.notice_period_days")}>
            <input
              type="number"
              min={0}
              max={3650}
              className="border rounded px-2 py-1 w-full"
              value={noticePeriodDays}
              onChange={(e) => setNoticePeriodDays(e.target.value)}
            />
          </Field>
          <Field label={t("insurance.form.coverage_sum")}>
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 w-full"
              value={coverageSumEur}
              onChange={(e) => setCoverageSumEur(e.target.value)}
            />
          </Field>
          <Field label={t("insurance.form.deductible")}>
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 w-full"
              value={deductibleEur}
              onChange={(e) => setDeductibleEur(e.target.value)}
            />
          </Field>
        </div>

        <h2 className="font-semibold pt-2 border-t mt-3">
          {t("insurance.form.value_section", {
            defaultValue: "Werthaltend? (z.B. fondsgebundene LV nach Beitragsphase)",
          })}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t("insurance.form.value_asset_class", {
              defaultValue: "Wert fließt in (leer = Risikoversicherung)",
            })}
          >
            <select
              className="border rounded px-2 py-1 w-full"
              value={valueAssetClass}
              onChange={(e) => setValueAssetClass(e.target.value as AssetClass | "")}
            >
              <option value="">
                {t("insurance.form.value_asset_class.none", {
                  defaultValue: "— Keine, reine Risikoversicherung",
                })}
              </option>
              <option value="ANLAGEN">
                {t("asset_class.ANLAGEN", { defaultValue: "Anlagen" })}
              </option>
              <option value="SONSTIGE">
                {t("asset_class.SONSTIGE", { defaultValue: "Sonstiges" })}
              </option>
              <option value="LIQUIDE">
                {t("asset_class.LIQUIDE", { defaultValue: "Liquide" })}
              </option>
              <option value="IMMOBILIE">
                {t("asset_class.IMMOBILIE", { defaultValue: "Immobilie" })}
              </option>
            </select>
          </Field>
          <div />
        </div>

        {valueAssetClass && (
          <div
            className="border rounded p-3 space-y-2"
            style={{ borderColor: "var(--border)" }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">
                {t("insurance.form.value_snapshots", {
                  defaultValue: "Wert-Zeitscheiben (z.B. Jahresmeldungen)",
                })}
              </h3>
              <button
                type="button"
                onClick={addSnapshotRow}
                className="text-xs px-2 py-1 rounded border"
                style={{ borderColor: "var(--border)" }}
              >
                + {t("insurance.form.add_snapshot", { defaultValue: "Zeile" })}
              </button>
            </div>
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              {t("insurance.form.value_snapshots_hint", {
                defaultValue:
                  "Stichtag + Wert pro Zeile. Lass leer, wenn du sie spaeter im Detail anlegst.",
              })}
            </p>
            {valueSnapshots.map((snap, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="date"
                  value={snap.asOf}
                  onChange={(e) => updateSnapshot(idx, { asOf: e.target.value })}
                  className="col-span-5 border rounded px-2 py-1 text-sm"
                />
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  value={snap.eur}
                  placeholder={t("insurance.form.value_eur_placeholder", {
                    defaultValue: "EUR",
                  })}
                  onChange={(e) => updateSnapshot(idx, { eur: e.target.value })}
                  className="col-span-6 border rounded px-2 py-1 text-sm num"
                />
                <button
                  type="button"
                  onClick={() => removeSnapshotRow(idx)}
                  disabled={valueSnapshots.length <= 1}
                  className="col-span-1 text-sm disabled:opacity-30"
                  style={{ color: "var(--neg)" }}
                  title={t("insurance.form.remove_snapshot", { defaultValue: "Zeile entfernen" })}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-semibold pt-2 border-t mt-3">{t("insurance.form.initial_premium")}</h2>
        <p className="text-xs" style={{ color: "var(--muted)" }}>
          {t("insurance.form.premium_hint", {
            defaultValue: "Beitragsfrei? Betrag auf 0 lassen.",
          })}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("insurance.form.premium_valid_from")}>
            <input
              type="month"
              required
              className="border rounded px-2 py-1 w-full"
              value={premiumValidFrom.slice(0, 7)}
              onChange={(e) => setPremiumValidFrom(`${e.target.value}-01`)}
            />
          </Field>
          <Field label={t("insurance.form.premium_amount")}>
            <input
              type="number"
              step="0.01"
              min={0}
              className="border rounded px-2 py-1 w-full"
              value={premiumAmountEur}
              onChange={(e) => setPremiumAmountEur(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field label={t("insurance.form.premium_rhythm")}>
            <select
              className="border rounded px-2 py-1 w-full"
              value={premiumRhythm}
              onChange={(e) => setPremiumRhythm(e.target.value as InsuranceRhythm)}
            >
              {RHYTHMS.map((r) => (
                <option key={r} value={r}>
                  {t(`insurance.rhythm.${r}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t("insurance.form.tags")}>
          <input
            type="text"
            className="border rounded px-2 py-1 w-full"
            placeholder={t("insurance.form.tags_placeholder")}
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
          />
        </Field>
        <Field label={t("insurance.form.notes")}>
          <textarea
            className="border rounded px-2 py-1 w-full"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        {error && <p className="text-red-700 text-sm">{error}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={create.isPending}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("insurance.form.submit")}
          </button>
          <Link to="/insurance" className="px-3 py-1 border rounded">
            {t("insurance.form.cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block text-sm">
      <span className="text-slate-600">{label}</span>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
